import React, { useState, forwardRef } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useField, useFormikContext } from "formik";

const SearchDropdown = forwardRef(
  ({ options, label, name, isDisabled, ...otherProps }, ref) => {
    const { values, setValues } = useFormikContext();
    const [field, meta] = useField(name);
    const [open, setOpen] = useState(false);

    const handleChange = (event, value) => {
      if (otherProps.onSelect) otherProps.onSelect(value);
      setValues({ ...values, [name]: value ? value.value : null });
      field.onChange(event);
      setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        setOpen(!open);
      }
    };

    const handleBlur = (event) => {
      field.onBlur(event);
    };

    const selectedOption = options.find(
      (option) => option.value === field.value
    );

    return (
      <Autocomplete
        disabled={isDisabled}
        options={options}
        getOptionLabel={(option) => option.label}
        value={selectedOption || null}
        onChange={handleChange}
        onBlur={field.onBlur}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onKeyDown={handleKeyDown}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={otherProps.required}
            error={meta.touched && !!meta.error}
            helperText={meta.touched && meta.error}
            onBlur={handleBlur}
            inputRef={ref}
            inputProps={{
              ...params.inputProps,
              "aria-label": label,
              "aria-required": otherProps.required,
              "aria-invalid": meta.touched && !!meta.error,
            }}
              sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                background: '#FFF',
                boxShadow: '0 1px 2px 0 rgba(23, 27, 34, 0.05)',
                '& fieldset': {
                  borderColor: '#DBE0E6',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: '#DBE0E6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#DBE0E6',
                  borderWidth: '1px',
                },
              },
              '& .MuiInputBase-input': {
                fontWeight: 400,
                lineHeight: '20px',
                color: '#252D38',
                '&::placeholder': {
                  color: '#47566A',
                  fontWeight: 300,
                  opacity: 1,
                },
              },
              '& .MuiInputLabel-root': {
                fontWeight: 400,
                color: '#252D38',
                '&.Mui-focused': {
                  color: '#252D38',
                },
              },
            }}
            {...otherProps}
          />
        )}
        componentsProps={{
          popper: {
            sx: {
              '& .MuiAutocomplete-listbox': {
                padding: 0,
                '& .MuiAutocomplete-option': {
                  fontWeight: 300,
                  color: '#252D38',
                  borderBottom: '1px solid #E5E7EB',
                  padding: '12px 16px',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                  },
                  '&[aria-selected="true"]': {
                    backgroundColor: '#F0F7FF',
                    fontWeight: 400,
                    '&:hover': {
                      backgroundColor: '#E6F2FF',
                    },
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#F9FAFB',
                  },
                },
              },
              '& .MuiPaper-root': {
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                marginTop: '4px',
              },
            },
          },
        }}
      />
    );
  }
);

export default SearchDropdown;
